import { useEffect, useMemo, type ReactNode } from 'react';
import { FormProvider, useForm, type FieldValues, type Path, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert } from 'antd';
import { Button, Flex, Modal } from 'pink-lava-ui';
import { FormBuilder } from '@/components/ui/form/FormBuilder';
import type { FormField } from '@/types/FieldSchema.type';
import type { FormModalMode } from '@/hooks/useFormModal';

interface FormModalProps<T extends FieldValues> {
  // Core Modal Props
  visible: boolean;
  mode: FormModalMode;
  validationMode?: 'onSubmit' | 'onBlur' | 'onChange' | 'onTouched' | 'all';
  title?: string;

  // Form Props
  fields: FormField<T>[];
  initialValues?: Partial<T>;
  validationSchema?: any;

  // Callbacks
  onSubmit: (_formValues: T, _mode: FormModalMode) => void | Promise<void>;
  onCancel: () => void;

  // UI Customization
  width?: number | string;
  loading?: boolean;
  submitButtonText?: {
    create?: string;
    update?: string;
  };
  cancelButtonText?: string;

  // Form Layout
  columns?: 1 | 2;
  showErrorMessage?: boolean;

  // Error handling
  errorMessage?: string;
  onClearError?: () => void;

  header?: ReactNode;
}

export const FormModal = <T extends FieldValues>({
  visible,
  mode,
  validationMode = 'all',
  title,
  fields,
  initialValues,
  validationSchema,
  onSubmit,
  onCancel,
  width = 480,
  loading = false,
  submitButtonText = {
    create: 'Simpan',
    update: 'Simpan',
  },
  cancelButtonText = 'Batalkan',
  showErrorMessage = true,
  onClearError,
  header,
}: FormModalProps<T>) => {
  // Form setup
  const methods = useForm<T>({
    resolver: validationSchema ? (zodResolver(validationSchema) as any) : undefined,
    defaultValues: initialValues as any,
    mode: validationMode,
  });

  const { handleSubmit, reset } = methods;

  // Reset form when modal opens or mode changes
  useEffect(() => {
    if (visible) {
      reset(initialValues as any);
      // Clear any existing errors when modal opens
      methods.clearErrors();
      onClearError?.();
    }
  }, [visible, mode, initialValues, reset, methods, onClearError]);

  // Process fields based on current mode
  const processedFields = useMemo(() => {
    return fields
      .filter(field => {
        // Filter by visibility
        if (field.visibleInModes && field.visibleInModes.length > 0) {
          return field.visibleInModes.includes(mode);
        }
        return true;
      })
      .map(field => {
        const processedField = { ...field };

        // Apply mode-based disability
        if (field.disabledInModes && field.disabledInModes.includes(mode)) {
          processedField.disabled = true;
        }

        // Apply mode-specific properties
        if (field.modeSpecific && field.modeSpecific[mode]) {
          Object.assign(processedField, field.modeSpecific[mode]);
        }

        return processedField;
      });
  }, [fields, mode]);

  // Handle form submission
  const handleFormSubmit: SubmitHandler<FieldValues> = async (formValues: FieldValues) => {
    try {
      await onSubmit(formValues as T, mode);
    } catch (error) {
      console.error('Form submission error:', error);

      // Check if this is a validation error with field-specific errors
      const apiResponse = (error as any)?.response?.data;
      const fieldValidationErrors = apiResponse?.data;
      const hasFieldErrors = fieldValidationErrors && typeof fieldValidationErrors === 'object';
      console.log({ hasFieldErrors });
      if (hasFieldErrors) {
        // Set field-specific errors (convert to lowercase)
        Object.keys(fieldValidationErrors).forEach(fieldName => {
          const errorMessage = fieldValidationErrors[fieldName];
          if (typeof errorMessage === 'string') {
            const lowerCaseFieldName = fieldName.toLowerCase();
            methods.setError(lowerCaseFieldName as Path<T>, {
              message: errorMessage,
            });
          }
        });

        // Don't set root error when there are field-specific errors
        // Only show field-specific errors, not the general Alert
      } else {
        // Handle general errors (non-validation errors)
        const errorMessage =
          apiResponse?.message ||
          apiResponse?.error ||
          (error as any)?.message ||
          'An error occurred. Please try again.';

        // Set the error to form state to display in Alert
        methods.setError('root', {
          message: errorMessage,
        });
      }
    }
  };

  const modalTitle = title || `${mode === 'create' ? 'Create' : 'Update'} Record`;
  const submitText = submitButtonText[mode] || (mode === 'create' ? 'Create' : 'Update');
  const formErrormessage = methods.formState.errors['root']?.message;

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onCancel}
      width={width}
      footer={() => <></>}
      destroyOnHidden
      centered
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {header}
          <div className="py-4">
            <FormBuilder fields={processedFields} methods={methods} showErrorMessage={showErrorMessage} />

            {formErrormessage && (
              <Alert message={formErrormessage} type="error" showIcon className="mt-4! text-red-500!" />
            )}
          </div>
          <Flex justify="end" gap="small" className="pt-4">
            <Button type="tertiary" onClick={onCancel} disabled={loading}>
              {cancelButtonText}
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} disabled={!methods.formState.isDirty}>
              {submitText}
            </Button>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
